// How services and package lines are charged, and the package price maths.
// Mirrors App\Models\Service::UNITS and Package::priceFor() on the server.

export type ChargeUnit = "fixed" | "guest" | "day" | "hour" | "piece";

export const CHARGE_UNITS: { value: ChargeUnit; label: string; per: string }[] = [
  { value: "fixed", label: "Fixed price", per: "" },
  { value: "guest", label: "Per guest", per: "guest" },
  { value: "day", label: "Per day", per: "day" },
  { value: "hour", label: "Per hour", per: "hour" },
  { value: "piece", label: "Per piece", per: "piece" },
];

export const toUnit = (value: unknown): ChargeUnit =>
  CHARGE_UNITS.some((u) => u.value === value) ? (value as ChargeUnit) : "fixed";

/** "/ guest", "/ day"… or "" for a fixed price */
export const unitSuffix = (unit: unknown): string => {
  const per = CHARGE_UNITS.find((u) => u.value === toUnit(unit))?.per;
  return per ? `/ ${per}` : "";
};

/** "per guest", "fixed"… for lists */
export const unitWords = (unit: unknown): string => {
  const u = toUnit(unit);
  return u === "fixed" ? "fixed" : `per ${CHARGE_UNITS.find((x) => x.value === u)!.per}`;
};

/** Margins below this % are flagged amber; below cost is red. */
export const MARGIN_WARN_PCT = 30;

export type MarginLevel = "loss" | "low" | "good" | "none";

export const marginOf = (price: number, cost: number): { amount: number; pct: number; level: MarginLevel } => {
  const amount = price - cost;
  if (price <= 0) return { amount, pct: 0, level: cost > 0 ? "loss" : "none" };
  const pct = (amount / price) * 100;
  return { amount, pct, level: amount < 0 ? "loss" : pct < MARGIN_WARN_PCT ? "low" : "good" };
};

export interface PricedLine {
  unit?: string | null;
  quantity?: number | string | null;
  unit_price?: number | string | null;
  cost_price?: number | string | null;
  is_optional?: boolean | null;
}

/** Quantity a line takes for an event: per-guest lines follow the guest count. */
export const lineQuantity = (line: PricedLine, guests: number): number => {
  const qty = Math.max(1, Number(line.quantity) || 1);
  return toUnit(line.unit) === "guest" ? qty * Math.max(1, guests || 1) : qty;
};

export type DiscountType = "percent" | "fixed" | null | undefined | "";

export const discountOn = (amount: number, type: DiscountType, value: number | string | null | undefined): number => {
  const v = Number(value) || 0;
  if (type === "percent") return (amount * Math.min(v, 100)) / 100;
  if (type === "fixed") return Math.min(v, amount);
  return 0;
};

/** What a package costs the client and us for a guest count (optional add-ons excluded). */
export const packagePrice = (
  lines: PricedLine[],
  guests: number,
  discountType?: DiscountType,
  discountValue?: number | string | null
) => {
  let list = 0;
  let cost = 0;
  for (const line of lines) {
    if (line.is_optional) continue;
    const q = lineQuantity(line, guests);
    list += q * (Number(line.unit_price) || 0);
    cost += q * (Number(line.cost_price) || 0);
  }
  const discount = discountOn(list, discountType, discountValue);
  return { list, discount, price: list - discount, cost, ...marginOf(list - discount, cost) };
};
